import { fetchEntityIfNeeded } from '@classpass/saga-tools';
import { call, put, select } from 'redux-saga/effects';
import * as logger from 'universal-logger';
import { trackGoogleTagManagerEvent as gtmPush } from 'components/GoogleTagManager/GoogleTagManager';
import { ExtendableError } from 'helpers/extendable-error';
import { isLoggedIn } from 'redux/saga/selectors';
import { selectUserLocation } from 'redux/modules/locale';
import {
  glsLocation,
  msas,
  promotionByAlias,
  userProfile,
  venueByIdV2,
  userPermissions,
} from 'redux/saga/entities/bound';

export function* msaByAlias(client, alias) {
  const allMsas = yield call(fetchEntityIfNeeded, msas, [client]);
  const msa = allMsas.filter(thisMsa => thisMsa.alias === alias)[0];
  if (!msa) {
    throw new ExtendableError(`Bad MSA alias ${alias}`, {
      code: 404,
      httpStatus: 404,
    });
  }
  return msa;
}

/* istanbul ignore next */
export const scrollToTop = () => {
  if (__CLIENT__) {
    window.scrollTo(0, 0);
    const contentEl = document.body.querySelector('.body__wrapper');
    if (contentEl) {
      contentEl.scrollTop = 0;
    }
  }
};

export function* getUserLocation(client) {
  try {
    const location = yield select(selectUserLocation);
    return location ? yield call(fetchEntityIfNeeded, glsLocation, [client, location.lat, location.lon]) : null;
  } catch (error) {
    yield call(logger.warn, 'Got error from GLS API', error);
    return null;
  }
}

export function* geodetectMsa(client) {
  const userLocation = yield call(getUserLocation, client);
  return userLocation ? userLocation.msa : null;
}

export function* promotionValidInMsa(client, msaIdentifier, promoAlias) {
  try {
    const promo = yield call(fetchEntityIfNeeded, promotionByAlias, [client, promoAlias, msaIdentifier]);
    return promo && promo.offers && promo.offers.length > 0;
  } catch (err) {
    return false;
  }
}

export function* venueAliasToId(client, alias) {
  const venue = yield call(fetchEntityIfNeeded, venueByIdV2, [client, alias, { extended: true }]);
  if (venue) {
    // If success, populate the datastore with this venue under the ID
    yield put(venueByIdV2.successAction({ data: venue, key: venue.id }));
    return venue.id;
  }
  return null;
}

export function* getUserId(client) {
  const loggedIn = yield select(isLoggedIn);
  if (loggedIn) {
    const profile = yield call(fetchEntityIfNeeded, userProfile, [client]);
    return profile.id;
  }
}

export function* userHasPermission(client, permission) {
  const userId = yield call(getUserId, client);
  if (userId) {
    const permissions = yield call(fetchEntityIfNeeded, userPermissions, [client, userId]);
    return !!permissions[permission];
  }
  return false;
}

/**
 * Adds a GTM event with a redirect callback, and adds fallbacks in case
 * GTM is not loaded or removed (by an adblocker, for instance).
 *
 * Should be called after tracking calls, but can be safely yielded as the last
 * effect in parallel with tracking events.
 */
export const gtmSafeRedirect = (url, timeout = 750) => {
  if (__CLIENT__ || __TEST__) {
    // If GTM is not loaded/removed, let's redirect immediately
    // because tags are not firing anyway.
    if (!window.google_tag_manager) {
      window.location.assign(url);
      return Promise.resolve();
    }

    const callbackPromise = new Promise(resolve =>
      gtmPush({
        event: 'redirect',
        eventCallback: resolve,
        eventTimeout: timeout,
      })
    );

    // Fallback timeout in case GTM is not available at this
    // point for some reason.
    const timeoutPromise = new Promise(resolve => setTimeout(resolve, timeout));

    return Promise.race([callbackPromise, timeoutPromise]).then(() => window.location.assign(url));
  }
};
